import DropdownMenu from 'common/components/drop-downs/drop-down-menu';
import { useMemo, useState } from 'react';
import { Segment, SegmentChangedPayload } from '../../segment-controls.types';
import DropdownMenuButton from 'common/components/drop-downs/dropdown-menu-button';
import { useGetSegmentsQuery } from 'app/apis/segmentation/segmentation-api';
import { useSelector, useDispatch } from 'react-redux';
import { selectSiteId } from 'features/topbar/selectedSiteSlice';
import {
  onSegmentChanged,
  selectSegmentCategories
} from '../../segment-controls.slice';
import { RootState } from 'app/store';
import { GetAllSegmentsDto } from 'app/apis/segmentation/segmentation-api.types';

interface SegmentControlSegmentDropdownProps {
  segment: Segment;
  componentId: string;
}

function getSegmentButtonLabel(
  segment: Segment,
  isFetching: boolean,
  allSegments: GetAllSegmentsDto[]
): string {
  if (isFetching) {
    return 'Getting Segments';
  }

  const label = segment.segmentCategory
    ? allSegments.find((s) => s.customDataKey === segment.segmentCategory)!
        .customDataKeyDisplayName
    : 'Select a segment';

  return label;
}

const SegmentDropdown = ({
  segment,
  componentId
}: SegmentControlSegmentDropdownProps) => {
  const dispatch = useDispatch();
  const siteId = useSelector(selectSiteId);
  const segmentCategories = useSelector((state: RootState) =>
    selectSegmentCategories(state, componentId)
  );
  const { isPocModeActive } = useSelector(
    (state: RootState) => state.pocToggle
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { data: segmentsFromApi, isFetching } = useGetSegmentsQuery({
    siteId,
    componentId
  });

  const allowedSegments = useMemo(() => {
    if (!segmentsFromApi) {
      return [];
    }

    return isPocModeActive
      ? segmentsFromApi
      : segmentsFromApi.filter((segment) => !segment.isPocOnly);
  }, [segmentsFromApi, isPocModeActive]);

  const segmentItems = useMemo(() => {
    const segmentsWhichHaveNotBeenSelected = allowedSegments.filter(
      (d) => !segmentCategories.some((category) => d.customDataKey === category)
    );

    const groupedSegments = segmentsWhichHaveNotBeenSelected.reduce<
      { key: string; values: GetAllSegmentsDto[] }[]
    >((groups, segment) => {
      const existingItem = groups.find(
        (group) => group.key === segment.segmentTypeDisplayName
      );

      if (existingItem) {
        existingItem.values = [...existingItem.values, segment];
        return groups;
      } else {
        return [
          ...groups,
          {
            key: segment.segmentTypeDisplayName,
            values: [segment]
          }
        ];
      }
    }, []);

    return groupedSegments;
  }, [allowedSegments, segmentCategories]);

  const dispatchSegmentCategoryChanged = (
    segment: Segment,
    newSegmentCategory: string
  ) => {
    const payload: SegmentChangedPayload = {
      componentId,
      segment: {
        ...segment,
        segmentCategory: newSegmentCategory
      }
    };
    dispatch(onSegmentChanged(payload));
  };

  const isSegmentMoneyPenny = (customDataKey: string) =>
    siteId === '5c0036e5e1af8' && customDataKey === 'Moneypenny';

  return (
    <DropdownMenu
      isDropdownOpen={isDropdownOpen}
      containerClasses="flex justify-center relative w-60"
      buttonClasses="flex text-sm items-center py-2.5 px-3 justify-center bg-white rounded border border-neutral-200 w-full max-w-60 flex-row-reverse px-3 py-2 bg-white"
      buttonIconClasses="ra-icons-generic-down-arrow text-bottom text-xs flex"
      buttonLabel={getSegmentButtonLabel(segment, isFetching, allowedSegments)}
      buttonLabelClasses="text-sm mr-2 capitalize"
      dropdownMenuClasses="z-40 top-11 left-0 w-60"
      isLoading={isFetching}
      onDropdownClicked={() => setIsDropdownOpen(!isDropdownOpen)}
      onClickAway={() => setIsDropdownOpen(false)}
    >
      {segmentItems.map((group, groupIndex) => (
        <div key={`group-${groupIndex}`}>
          <div className="text-sm pl-1 border-b border-neutral-200 mt-3 text-neutral-500">
            {group.key}
          </div>
          {group.values.map((segmentDto, customDataKeyIndex) => (
            <DropdownMenuButton
              key={`group-${groupIndex}-custom-data-key-${customDataKeyIndex}`}
              identifier="enable-webhook"
              buttonClasses="flex items-center w-full"
              onClick={() => {
                dispatchSegmentCategoryChanged(
                  segment,
                  segmentDto.customDataKey
                );
                setIsDropdownOpen(false);
              }}
            >
              {isSegmentMoneyPenny(segmentDto.customDataKey) ? (
                <img
                  className="h-6 w-6 mr-2"
                  src="/media/logos/moneypenny-logo.png"
                />
              ) : (
                <></>
              )}
              <span className="capitalize text-sm">
                {segmentDto.customDataKeyDisplayName}
              </span>
            </DropdownMenuButton>
          ))}
        </div>
      ))}
    </DropdownMenu>
  );
};
export default SegmentDropdown;
